import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const ScheduleSchema = Yup.object().shape({
  scheduleType: Yup.string()
    .oneOf(["delay", "specific_time", "recurring"], "Tipo inválido")
    .required("Selecione o tipo de agendamento"),
  delayMinutes: Yup.number().when("scheduleType", {
    is: "delay",
    then: (schema) =>
      schema
        .min(1, "Mínimo 1 minuto")
        .max(10080, "Máximo 7 dias")
        .required("Digite o tempo em minutos"),
    otherwise: (schema) => schema.notRequired(),
  }),
  specificTime: Yup.string().when("scheduleType", {
    is: "specific_time",
    then: (schema) => schema.required("Digite a hora"),
    otherwise: (schema) => schema.notRequired(),
  }),
  recurringDays: Yup.string().when("scheduleType", {
    is: "recurring",
    then: (schema) => schema.required("Selecione os dias"),
    otherwise: (schema) => schema.notRequired(),
  }),
  recurringTime: Yup.string().when("scheduleType", {
    is: "recurring",
    then: (schema) => schema.required("Digite a hora"),
    otherwise: (schema) => schema.notRequired(),
  }),
  message: Yup.string().required("Digite a mensagem"),
});

const FlowBuilderScheduleModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    scheduleType: "delay",
    delayMinutes: 60,
    specificTime: "09:00",
    recurringDays: "1,2,3,4,5",
    recurringTime: "09:00",
    message: "",
  };

  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Adicionar Agendamento",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Agendamento",
        btn: "Salvar",
      });
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Agendamento",
        btn: "Adicionar",
      });
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = (values) => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: values,
      });
    } else if (open === "create") {
      handleClose();
      onSave(values);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={data?.data || initialState}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={handleSave}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers>
                <Grid style={{ width: "100%", marginTop: 40 }} container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>Tipo de Agendamento</Typography>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <Field
                        as={Select}
                        name="scheduleType"
                        style={{ width: "95%" }}
                      >
                        <MenuItem value="delay">Após X minutos</MenuItem>
                        <MenuItem value="specific_time">Hora específica (hoje)</MenuItem>
                        <MenuItem value="recurring">Recorrente (dias da semana)</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {values.scheduleType === "delay" && (
                    <Grid item xs={12}>
                      <Typography>Minutos para Agendar</Typography>
                      <Field
                        as={TextField}
                        name="delayMinutes"
                        type="number"
                        error={touched.delayMinutes && Boolean(errors.delayMinutes)}
                        helperText={touched.delayMinutes && errors.delayMinutes || "1 minuto a 7 dias (10080 minutos)"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        placeholder="60"
                      />
                    </Grid>
                  )}

                  {values.scheduleType === "specific_time" && (
                    <Grid item xs={12}>
                      <Typography>Hora (HH:MM)</Typography>
                      <Field
                        as={TextField}
                        name="specificTime"
                        type="time"
                        error={touched.specificTime && Boolean(errors.specificTime)}
                        helperText={touched.specificTime && errors.specificTime}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}

                  {values.scheduleType === "recurring" && (
                    <>
                      <Grid item xs={12}>
                        <Typography>Dias da Semana</Typography>
                        <Field
                          as={TextField}
                          name="recurringDays"
                          error={touched.recurringDays && Boolean(errors.recurringDays)}
                          helperText={touched.recurringDays && errors.recurringDays || "1=Seg, 2=Ter, 3=Qua, 4=Qui, 5=Sex, 6=Sab, 0=Dom (ex: 1,2,3,4,5)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="1,2,3,4,5"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography>Hora (HH:MM)</Typography>
                        <Field
                          as={TextField}
                          name="recurringTime"
                          type="time"
                          error={touched.recurringTime && Boolean(errors.recurringTime)}
                          helperText={touched.recurringTime && errors.recurringTime}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Typography>Mensagem</Typography>
                    <Field
                      as={TextField}
                      name="message"
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message || "Mensagem a ser enviada"}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Digite a mensagem"
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderScheduleModal;
